/**
 * Transforms pre-approval data from backend format to frontend format
 */
export const transformPreApprovalToFormData = (response, failureModeOptions = []) => {
    let preApproval;
    if (response.isSuccessful !== undefined && Array.isArray(response.data)) {
        preApproval = response.data[0];
    } else {
        preApproval = response;
    }

    return {
        preAppNo: preApproval.preAppNo,
        warrantyEndDate: preApproval.warrantyEndDate,
        warrantyStartDate: preApproval.warrantyStartDate || preApproval.saleDate || null,
        warrantyEndKm: preApproval.warrantyEndKm,
        currentKm: preApproval.currentKm,
        periodicMaintance: preApproval.periodicMaintance,
        partCode: preApproval.partCode,
        partName: preApproval.partName,
        repairDate: preApproval.repairDate,
        plateNo: preApproval.plateNo || '',
        saleDate: preApproval.saleDate || null,
        id: preApproval.id || 0,
        failureDate: preApproval.failureDate ? new Date(preApproval.failureDate) : null,
        failureKM: preApproval.currentKm || '',
        chassisNo: preApproval.chassisNo || '',
        estimatedPartCost: preApproval.estimatedPartCost || 0,
        estimatedLaborCost: preApproval.estimatedLaborCost || 0,
        otherCosts: preApproval.otherCosts || 0,
        totalCosts: preApproval.totalAmount || preApproval.totalCosts || 0,
        approvalNote: preApproval.approvalNote || '',
        customerStatement: preApproval.custStatement || '',
        failureReason: preApproval.causeOfailure || '',
        resolutionSummary: preApproval.remedyOresults || '',
        existingFileUrl: preApproval.existingFileUrl || '',
        itemCode: preApproval.itemCode ? { id: preApproval.itemCode.id, itemCode: preApproval.itemCode.itemCode } : null,
        failureMode: preApproval.failureMode || null,
        mainCategory: preApproval.mainCategory || '',
        secondCategory: preApproval.secondCategory || '',
        subCategory: preApproval.subCategory || '',
        subPartCategory: preApproval.subPartCategory || '',
        vehicleItemCode: preApproval.vehicleItemCode || '',
        status: preApproval.status,
        modifiedName: preApproval.modifiedName,
        modifiedDate: preApproval.modifiedDate,
        countryCode: preApproval.countryCode || '',
        dealerCode: preApproval.dealerCode || preApproval.bosBranchSiteCode || '',
        dealerName: preApproval.dealerName || ''
    };
};

/**
 * Formats a date for display in the table
 */
export const formatDate = (rowData, field) => {
    if (!rowData[field]) return '-';
    return new Date(rowData[field]).toLocaleDateString('tr-TR');
};

/**
 * Prepares pre-approval data for API submission
 * Ensures correct types for C# model binding
 */
export const preparePreApprovalForSubmission = (formData, isEdit, currentPreApproval) => {
    const {
        failureDate, failureKM, chassisNo,
        estimatedPartCost, estimatedLaborCost, otherCosts, totalCosts,
        approvalNote, customerStatement, failureReason, resolutionSummary,
        partCode, vehicleItemCode, failureMode,
        mainCategory, secondCategory, subCategory, subPartCategory,
        bosBranchCode, bosBranchSiteCode, statusDesc, status, preAppNo
    } = formData;

    // Helper: safely parse category value to int or null
    const parseCategoryId = (val) => {
        if (val === null || val === undefined || val === '') return null;
        if (typeof val === 'object' && val !== null) return val.id || null;
        if (typeof val === 'string') {
            const parts = val.split(' - ');
            const parsed = parseInt(parts[0], 10);
            return isNaN(parsed) ? null : parsed;
        }
        if (typeof val === 'number') return val;
        return null;
    };

    // Helper: resolve itemCode — backend expects int (not null, not object)
    const resolveItemCode = () => {
        const ic = formData.itemCode;
        if (ic === null || ic === undefined) return null;
        if (typeof ic === 'object' && ic !== null) return ic.id || 0;
        if (typeof ic === 'number') return ic;
        const parsed = parseInt(ic, 10);
        return isNaN(parsed) ? 0 : parsed;
    };
    
    // Helper: resolve failureMode — backend expects int? 
    const resolveFailureMode = () => {
        const fm = formData.failureMode;
        if (fm === null || fm === undefined) return null;
        if (typeof fm === 'object' && fm !== null) return fm.id || null;
        if (typeof fm === 'number') return fm;
        const parsed = parseInt(fm, 10);
        return isNaN(parsed) ? null : parsed;
    };

    const preApprovalData = {
        chassisNo: chassisNo || '',
        failureDate: failureDate ? (typeof failureDate === 'string' ? failureDate : failureDate.toISOString()) : null,
        kmInfo: failureKM ? parseInt(failureKM, 10) || 0 : 0,
        description: approvalNote || '',
        estimatedTrackPrice: estimatedPartCost || 0,
        estimatedLaborPrice: estimatedLaborCost || 0,
        otherPayments: otherCosts || 0,
        totalPrice: totalCosts || 0,
        custStatement: customerStatement || '',
        causeOfailure: failureReason || '',
        remedyOresults: resolutionSummary || '',
        bosBranchCode: bosBranchCode || '',
        bosBranchSiteCode: bosBranchSiteCode || '',
        preAppNo: preAppNo || '',
        statusDesc: statusDesc || '',
        status: status || 10,
        itemCode: resolveItemCode(),
        mainCategory: parseCategoryId(mainCategory),
        secondaryCategory: parseCategoryId(secondCategory),
        subCategory: parseCategoryId(subCategory),
        subPartCategory: parseCategoryId(subPartCategory),
        failureMode: resolveFailureMode(),
        partCode: partCode?.code || partCode || '',
        vehicleItemCode: vehicleItemCode || ''
    };

    if (isEdit && currentPreApproval) {
        return {
            ...preApprovalData,
            id: currentPreApproval.id,
            modifiedBy: 1,
            modifiedDate: new Date().toISOString(),
            lastUpdateSession: 'web'
        };
    } else {
        return {
            ...preApprovalData,
            createdBy: 1,
            createdDate: new Date().toISOString(),
            createdSession: 'web'
        };
    }
};

/**
 * Get pre-approvals filtered by tab
 */
export const getPreApprovalsByTab = (preApprovals, activeTab) => {
    if (!preApprovals) return [];

    const tabToStatusMap = { 0: 10, 1: 20, 2: 15, 3: 30, 4: 40 };
    const statusTextMap = {
        10: 'Taslak', 15: 'İade Edildi', 20: 'Onay Bekliyor', 30: 'Onaylandı', 40: 'Reddedildi'
    };

    const transformedItems = preApprovals.map((item) => ({
        ...item,
        startDate: item.startDate || null,
        endDate: item.endDate || null,
        chassisNo: item.chassisNo || '',
        preApprovalNo: item.preApprovalNo || item.preAppNo || '',
        serial: item.serial || '',
        engineNumber: item.engineNumber || '',
        invoiceNo: item.invoiceNo || '',
        estimatedPartCost: item.estimatedPartCost || item.estimatedTrackPrice || 0,
        estimatedLaborCost: item.estimatedLaborCost || item.estimatedLaborPrice || 0,
        otherCosts: item.otherCosts || item.otherPayments || 0,
        totalAmount: item.totalAmount || item.totalPrice || 0,
        currentKm: item.currentKm || item.kmInfo || 0,
        repairDate: item.repairDate || item.failureDate || null,
        dealerName: item.dealerName || '',
        dealerCode: item.dealerCode || item.bosBranchSiteCode || '',
        countryCode: item.countryCode || '',
        plateNo: item.plateNo || '',
        vehicleItemCode: item.vehicleItemCode || null,
        modelYear: item.modelYear || '',
        customerName: item.customerName || '',
        brand: item.brand || '',
        type: item.type || '',
        model: item.model || '',
        variant: item.variant || '',
        class: item.class || '',
        version: item.version || '',
        saleDate: item.saleDate || null,
        warrantyEndDate: item.warrantyEndDate || null,
        warrantyStartDate: item.warrantyStartDate || item.saleDate || null,
        warrantyEndKm: item.warrantyEndKm || '',
        fProductGroup: item.fProductGroup || '',
        partCode: item.partCode || '',
        partName: item.partName || '',
        status: item.status || 0,
        statusText: statusTextMap[item.status] || item.statusDesc || 'Bilinmiyor'
    }));

    if (activeTab !== undefined) {
        return transformedItems.filter((item) => item.status === tabToStatusMap[activeTab]);
    }
    return transformedItems;
};
